import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { RolePageLayout } from '../components/layout/RolePageLayout';
import { Card, Badge, Button, Input, Spinner } from '../components/ui';
import { orderApi } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useAdminSocket } from '../hooks/useAdminSocket';

export default function OperatorDashboard() {
  const { t } = useTranslation();
  useAuth();
  const [searchTerm, setSearchTerm] = useState('');
  const [qrToken, setQrToken] = useState('');
  const [pendingOrders, setPendingOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [cylinderSerial, setCylinderSerial] = useState('');
  const [doneToday, setDoneToday] = useState(0);

  const countDoneToday = (orders) => {
    const start = new Date();
    start.setHours(0, 0, 0, 0);
    return (orders || []).filter((o) => {
      const status = String(o.status || '').toUpperCase();
      if (!['DELIVERED', 'COMPLETED', 'DONE'].includes(status)) return false;
      const at = new Date(o.updatedAt || o.completedAt || o.createdAt || 0);
      return at >= start;
    }).length;
  };

  const syncOfflineActions = useCallback(async () => {
    const offline = JSON.parse(localStorage.getItem('offline_completions') || '[]');
    if (offline.length === 0) return;

    const remaining = [];
    for (const action of offline) {
      try {
        if (action.type === 'phone') await orderApi.completeByPhone(action.phone, action.serials);
        else await orderApi.completeByQr(action.token, action.serials);
      } catch (err) {
        remaining.push(action);
      }
    }
    localStorage.setItem('offline_completions', JSON.stringify(remaining));
    if (offline.length > remaining.length) toast.success(t('operatorDashboard.toastOfflineSynced', { count: offline.length - remaining.length }));
  }, [t]);

  const fetchPending = useCallback(async () => {
    try {
      const data = await orderApi.getFullQueue('active');
      const list = Array.isArray(data) ? data : [];
      setPendingOrders(list);
      setDoneToday(countDoneToday(list));
      // Auto-sync offline actions if online
      syncOfflineActions();
    } catch (err) {
      console.error('Queue fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, [syncOfflineActions]);

  useAdminSocket({ onQueueUpdate: fetchPending });

  useEffect(() => {
    fetchPending();
    const interval = setInterval(fetchPending, 10000);
    return () => clearInterval(interval);
  }, [fetchPending]);

  const handleCompleteByPhone = async (e) => {
    e.preventDefault();
    if (!searchTerm) return;
    setProcessing(true);
    const serials = cylinderSerial ? [cylinderSerial] : [];
    try {
      await orderApi.completeByPhone(searchTerm, serials);
      toast.success(t('operatorDashboard.toastGasDelivered'));
      setDoneToday((d) => d + 1);
      setSearchTerm('');
      setCylinderSerial('');
      fetchPending();
    } catch (err) {
      if (!navigator.onLine || err.message.includes('fetch')) {
        const offline = JSON.parse(localStorage.getItem('offline_completions') || '[]');
        offline.push({ type: 'phone', phone: searchTerm, serials, time: Date.now() });
        localStorage.setItem('offline_completions', JSON.stringify(offline));
        toast.info(t('operatorDashboard.toastOfflineNoInternet'));
        setSearchTerm('');
        setCylinderSerial('');
      } else {
        toast.error(err.message);
      }
    } finally {
      setProcessing(false);
    }
  };

  const handleCompleteByQr = async (e) => {
    e.preventDefault();
    if (!qrToken) return;
    setProcessing(true);
    const serials = cylinderSerial ? [cylinderSerial] : [];
    try {
      await orderApi.completeByQr(qrToken, serials);
      toast.success(t('operatorDashboard.toastQrConfirmed'));
      setDoneToday((d) => d + 1);
      setQrToken('');
      setCylinderSerial('');
      fetchPending();
    } catch (err) {
      if (!navigator.onLine || err.message.includes('fetch')) {
        const offline = JSON.parse(localStorage.getItem('offline_completions') || '[]');
        offline.push({ type: 'qr', token: qrToken, serials, time: Date.now() });
        localStorage.setItem('offline_completions', JSON.stringify(offline));
        toast.info(t('operatorDashboard.toastOfflineSaved'));
        setQrToken('');
        setCylinderSerial('');
      } else {
        toast.error(err.message);
      }
    } finally {
      setProcessing(false);
    }
  };

  const quickComplete = async (orderId) => {
    setProcessing(true);
    try {
      await orderApi.completeByQr(pendingOrders.find(o => o.id === orderId).qrCodeToken, cylinderSerial ? [cylinderSerial] : []);
      const order = pendingOrders.find((o) => o.id === orderId);
      toast.success(t('operatorDashboard.toastCompleted'));
      setDoneToday((d) => d + (order?.quantity || 1));
      fetchPending();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <RolePageLayout role={t('operatorDashboard.role')}>
        <div className="h-screen flex items-center justify-center"><Spinner size="lg" /></div>
      </RolePageLayout>
    );
  }

  return (
    <RolePageLayout
      role={t('operatorDashboard.role')}
      headerRight={
        <Badge variant="green" className="py-2 px-3.5 text-xs shadow-[0_6px_16px_rgba(16,185,129,0.12)]">
          {t('operatorDashboard.pendingBadge', { count: pendingOrders.length })}
        </Badge>
      }
    >
      <div className="max-w-[1180px] mx-auto px-6 md:px-9 py-8">
        <div className="mb-8">
          <h1 className="sg-h1 text-[38px]">
            {t('operatorDashboard.titlePrefix')} <span className="text-[var(--brand-green)]">{t('operatorDashboard.titleSuffix')}</span>
          </h1>
          <p className="text-[var(--fg3)] font-medium mt-1.5 text-[15px]">
            {t('operatorDashboard.subtitle')}
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[360px_1fr] gap-8 items-start">
          {/* Action Panel */}
          <div className="space-y-5">
            <Card className="p-6 border-2 border-[var(--border)] shadow-lg">
              <h3 className="text-[17px] font-black mb-5 flex items-center gap-2 uppercase tracking-wide">
                <span className="material-symbols-outlined text-[var(--brand-green)]">qr_code_scanner</span>
                {t('operatorDashboard.confirmTitle')}
              </h3>

              <div className="space-y-6">
                {/* QR Scanner Placeholder */}
                <form onSubmit={handleCompleteByQr} className="space-y-3">
                  <Input
                    label={t('operatorDashboard.qrLabel')}
                    placeholder="SMART_ABCD1234"
                    value={qrToken}
                    onChange={(e) => setQrToken(e.target.value)}
                    icon="qr_code"
                  />
                  <Button type="submit" loading={processing} className="w-full" variant="emerald">
                    {t('operatorDashboard.qrConfirm')}
                  </Button>
                </form>

                <div className="relative py-4">
                  <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-slate-100 dark:border-slate-800"></div></div>
                  <div className="relative flex justify-center text-xs uppercase"><span className="bg-white dark:bg-slate-900 px-2 text-slate-400 font-bold tracking-widest">{t('auth.orDivider')}</span></div>
                </div>

                {/* Phone Search */}
                <form onSubmit={handleCompleteByPhone} className="space-y-3">
                  <Input
                    label={t('operatorDashboard.phoneLabel')}
                    placeholder="+998 90 123 45 67"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    icon="phone"
                  />
                  <Button type="submit" loading={processing} className="w-full" variant="blue">
                    {t('operatorDashboard.phoneConfirm')}
                  </Button>
                </form>

                <div className="pt-4 border-t border-slate-100 dark:border-slate-800">
                  <Input
                    label={t('operatorDashboard.serialLabel')}
                    placeholder="SG-88001-TX"
                    value={cylinderSerial}
                    onChange={(e) => setCylinderSerial(e.target.value)}
                    icon="propane_tank"
                  />
                </div>
              </div>
            </Card>

            {/* Stats Card */}
            <Card className="p-6 bg-gradient-to-br from-[#1e9f6b] to-[#0d7a52] text-white border-0 shadow-[var(--shadow-glow-green)]">
              <div className="flex justify-between items-start mb-3.5">
                <span className="material-symbols-outlined text-[40px]">trending_up</span>
                <Badge variant="amber">{t('main.today')}</Badge>
              </div>
              <p className="text-[10px] font-black uppercase tracking-[0.18em] text-white/80">{t('operatorDashboard.totalDelivered')}</p>
              <h2 className="text-[30px] font-black mt-1">
                {doneToday}{' '}
                <span className="text-sm font-medium text-white/80">{t('operatorDashboard.cylinderUnit')}</span>
              </h2>
              <div className="mt-5 h-[5px] w-full bg-white/30 rounded-full overflow-hidden">
                <div
                  className="h-full bg-white transition-all duration-400"
                  style={{ width: `${Math.min(100, 40 + doneToday)}%` }}
                />
              </div>
            </Card>
          </div>

          <div>
            <Card className="p-8 border-2 border-[var(--border)] shadow-lg min-h-[580px]">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-xl font-black flex items-center gap-3 italic uppercase tracking-tight">
                  <span className="material-symbols-outlined text-[var(--accent-blue)]">list_alt</span>
                  {t('operatorDashboard.queueTitle')}
                </h3>
                <button onClick={fetchPending} className="text-slate-400 hover:text-[#1e9f6b] transition-colors">
                  <span className="material-symbols-outlined">refresh</span>
                </button>
              </div>

              <div className="space-y-4">
                <AnimatePresence mode="popLayout">
                  {pendingOrders.length === 0 ? (
                    <motion.div 
                      initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                      className="flex flex-col items-center justify-center py-20 text-slate-400"
                    >
                      <span className="material-symbols-outlined text-[64px] mb-4 opacity-20">hourglass_empty</span>
                      <p className="font-bold italic uppercase tracking-widest text-sm">{t('operatorDashboard.emptyQueue')}</p>
                    </motion.div>
                  ) : (
                    pendingOrders.map((order, idx) => (
                      <motion.div
                        key={order.id}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, scale: 0.95 }}
                        transition={{ delay: idx * 0.05 }}
                        className="group"
                      >
                        <div className={`p-[18px] rounded-3xl border-2 transition-all duration-300 flex flex-col md:flex-row items-center gap-4 md:gap-5 ${order.isPriority ? 'border-[#fde68a] bg-amber-500/[0.06]' : 'border-[var(--border)] bg-[var(--bg-subtle)]'}`}>
                          <div className={`h-[52px] w-[52px] rounded-2xl flex items-center justify-center font-black text-xl shadow-md shrink-0 ${order.isPriority ? 'bg-[var(--accent-amber)] text-white' : 'bg-white text-[var(--fg4)]'}`}>
                            {idx + 1}
                          </div>

                          <div className="flex-1 flex items-center gap-4">
                            <div className="h-12 w-12 rounded-2xl bg-slate-100 dark:bg-slate-800 flex items-center justify-center overflow-hidden shadow-inner">
                              {order.user?.avatarUrl ? (
                                <img 
                                  src={order.user.avatarUrl} 
                                  alt={order.user.name} 
                                  className="h-full w-full object-cover"
                                  onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
                                />
                              ) : null}
                              <div className={`h-full w-full items-center justify-center font-black text-lg text-slate-400 ${order.user?.avatarUrl ? 'hidden' : 'flex'}`}>
                                {order.user?.name?.[0] || 'M'}
                              </div>
                            </div>
                            <div>
                              <div className="flex items-center gap-3 mb-1">
                                <h4 className="font-black text-slate-900 dark:text-white text-lg">{order.user?.name || t('operatorDashboard.customer')}</h4>
                                {order.isPriority && <Badge variant="amber" className="text-[10px]">{t('driverDashboard.priority').toUpperCase()}</Badge>}
                              </div>
                              <div className="flex gap-4 text-sm font-bold text-slate-500">
                                <span className="flex items-center gap-1"><span className="material-symbols-outlined text-[16px]">phone</span>{order.user?.phone}</span>
                                <span className="flex items-center gap-1"><span className="material-symbols-outlined text-[16px]">propane_tank</span>{t('driverDashboard.count', { count: order.quantity })}</span>
                              </div>
                            </div>
                          </div>

                          <div className="text-right shrink-0">
                            <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">{t('operatorDashboard.price')}</p>
                            <p className="font-black text-lg text-slate-900 dark:text-white">{(order.totalPrice || 0).toLocaleString()} {t('common.uzs')}</p>
                          </div>

                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              variant="emerald"
                              className="rounded-xl px-6 h-12"
                              onClick={() => quickComplete(order.id)}
                              loading={processing}
                            >
                              {t('operatorDashboard.deliveredBtn')}
                            </Button>
                          </div>
                        </div>
                      </motion.div>
                    ))
                  )}
                </AnimatePresence>
              </div>
            </Card>
          </div>
        </div>
      </div>
    </RolePageLayout>
  );
}
